<script setup lang="ts">
import { QLinearProgress } from 'quasar'
import { computed, onMounted, onUnmounted, ref } from 'vue'
import { Color } from '@previo/codebooks'
import { Assert } from '@previo/core'
import { PCardRound, PCardSize } from '../card/types'
import PTextStandard from '../typography/body/PTextStandard.vue'
import { PSpaceSize } from '../space/types'
import PCard from '../card/PCard.vue'
import PIcon from '../icon/PIcon.vue'
import PSpace from '../space/PSpace.vue'
import { usePrevioDS } from '../../composables/helpers'
import { PToastVariant } from './types'

type Props = {
  variant: PToastVariant
  title: string
  timeout?: number
}

type Emits = {
  (e: 'close', evt: Event): void
}

const props = withDefaults(defineProps<Props>(), {
  timeout: 5000,
})
const emits = defineEmits<Emits>()

const ds = usePrevioDS()

const PROGRESS_UPDATE_INTERVAL = 20

const progress = ref(1)
const isPaused = ref(false)

let timeoutId: ReturnType<typeof setTimeout> | null = null
let intervalId: ReturnType<typeof setInterval> | null = null
let remainingTime = 0
let startTime = 0

const clearTimers = (): void => {
  if (timeoutId) {
    clearTimeout(timeoutId)
    timeoutId = null
  }
  if (intervalId) {
    clearInterval(intervalId)
    intervalId = null
  }
}

const handleTimeout = (): void => {
  clearTimers()
  progress.value = 0
  emits('close', new Event('timeout'))
}

const onClose = (evt: Event): void => {
  clearTimers()
  emits('close', evt)
}

const startTimer = (timeLeft: number, initialProgress = 1): void => {
  if (timeLeft <= 0) {
    handleTimeout()
    return
  }

  startTime = Date.now()
  remainingTime = timeLeft
  progress.value = initialProgress

  intervalId = setInterval(() => {
    if (isPaused.value) return

    const elapsed = Date.now() - startTime
    const remaining = Math.max(0, initialProgress * (1 - elapsed / remainingTime))
    progress.value = remaining

    if (remaining <= 0) {
      handleTimeout()
    }
  }, PROGRESS_UPDATE_INTERVAL)

  timeoutId = setTimeout(() => {
    handleTimeout()
  }, remainingTime)
}

const pauseTimer = (): void => {
  if (isPaused.value) return

  isPaused.value = true
  const elapsed = Date.now() - startTime
  remainingTime = Math.max(0, remainingTime - elapsed)
  clearTimers()
}

const resumeTimer = (): void => {
  if (!isPaused.value || remainingTime <= 0) return

  isPaused.value = false
  startTimer(remainingTime, progress.value)
}

onMounted(() => {
  if (props.timeout && props.timeout > 0) {
    startTimer(props.timeout)
  }
})

onUnmounted(() => {
  clearTimers()
})

const bgColor = computed<
  | Color.POSITIVE_TRANSPARENT
  | Color.NEGATIVE_TRANSPARENT
  | Color.WARNING_TRANSPARENT
  | Color.INFO_TRANSPARENT
>(() => {
  switch (props.variant) {
    case PToastVariant.POSITIVE:
      return Color.POSITIVE_TRANSPARENT
    case PToastVariant.NEGATIVE:
      return Color.NEGATIVE_TRANSPARENT
    case PToastVariant.WARNING:
      return Color.WARNING_TRANSPARENT
    case PToastVariant.INFO:
      return Color.INFO_TRANSPARENT
    default:
      return Assert.unreachable(props.variant)
  }
})

const textColor = computed<
  Color.POSITIVE_DARK | Color.NEGATIVE_DARK | Color.WARNING_DARK | Color.INFO_DARK
>(() => {
  switch (props.variant) {
    case PToastVariant.POSITIVE:
      return Color.POSITIVE_DARK
    case PToastVariant.NEGATIVE:
      return Color.NEGATIVE_DARK
    case PToastVariant.WARNING:
      return Color.WARNING_DARK
    case PToastVariant.INFO:
      return Color.INFO_DARK
    default:
      return Assert.unreachable(props.variant)
  }
})

const timeoutColor = computed<Color.POSITIVE | Color.NEGATIVE | Color.WARNING | Color.INFO>(() => {
  switch (props.variant) {
    case PToastVariant.POSITIVE:
      return Color.POSITIVE
    case PToastVariant.NEGATIVE:
      return Color.NEGATIVE
    case PToastVariant.WARNING:
      return Color.WARNING
    case PToastVariant.INFO:
      return Color.INFO
    default:
      return Assert.unreachable(props.variant)
  }
})

const icon = computed<string>(() => {
  switch (props.variant) {
    case PToastVariant.POSITIVE:
      return 'circle-check'
    case PToastVariant.NEGATIVE:
      return 'circle-xmark'
    case PToastVariant.WARNING:
      return 'circle-exclamation'
    case PToastVariant.INFO:
      return 'circle-info'
    default:
      return Assert.unreachable(props.variant)
  }
})

const isMobilePositive = computed<boolean>(() => {
  return props.variant === PToastVariant.POSITIVE && ds.screen().isMobile
})
</script>

<template>
  <p-card
    v-if="isMobilePositive"
    class="ds-toast-mobile-positive-layout p-display-flex p-justify-center p-items-center"
    :pa-mobile="PCardSize.XS"
    :color="bgColor"
    :round="PCardRound.LG"
    @click="onClose"
  >
    <p-icon :icon="icon" :color="textColor" />
    <p-space :mobile="PSpaceSize.XS" />
    <p-text-standard :text="title" :color="textColor" />
    <p-space :mobile="PSpaceSize.XS" />
  </p-card>

  <p-card
    v-else
    :color="bgColor"
    :round="PCardRound.MD"
    :pa-mobile="PCardSize.MD"
    class="ds-toast"
    @mouseenter="pauseTimer"
    @mouseleave="resumeTimer"
  >
    <div class="p-display-flex p-no-wrap">
      <p-icon :icon="icon" :color="textColor" />
      <p-space :mobile="PSpaceSize.SM" />
      <p-text-standard :text="title" :color="textColor" />
    </div>

    <p-icon
      icon="xmark"
      :color="textColor"
      class="ds-toast__close p-cursor-pointer"
      @click="onClose"
    />

    <q-linear-progress
      v-if="timeout && timeout > 0"
      :value="progress"
      class="ds-toast__timeout"
      :color="timeoutColor"
      track-color="transparent"
      instant-feedback
    />
  </p-card>
</template>

<style scoped lang="scss">
.ds-toast {
  width: 280px;
  padding-right: 32px;

  &__close {
    position: absolute;
    top: 8px;
    right: 8px;
  }

  &__timeout {
    position: absolute;
    left: 0;
    right: 0;
    bottom: 0;
    display: block;
  }
}

.ds-toast-mobile-positive-layout {
  width: fit-content;
  min-width: 128px;
}
</style>
